import { useState, type FormEvent } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ApiError, type components } from '../../api/client'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'

// DIS-30: Settings › Invites (admin). Create links, copy once, list, revoke.
export const Route = createFileRoute('/_app/settings/invites')({
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'admin') throw redirect({ to: '/' })
  },
  component: InvitesPage,
})

type InviteCreated = components['schemas']['InviteCreated']

const invitesQuery = queryOptions({
  queryKey: ['admin', 'invites'],
  queryFn: async () => {
    const { data, response } = await api.GET('/admin/invites')
    if (!data) throw new Error(`GET /admin/invites failed: ${response.status}`)
    return data.invites
  },
})

function InvitesPage() {
  const qc = useQueryClient()
  const invites = useQuery(invitesQuery)
  const [created, setCreated] = useState<InviteCreated | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const create = useMutation({
    mutationFn: async (body: components['schemas']['CreateInvite']) => {
      const { data, error: err, response } = await api.POST('/admin/invites', { body })
      if (!data) throw new Error((err as ApiError | undefined)?.message ?? `failed (${response.status})`)
      return data
    },
    onSuccess: (data) => {
      setCreated(data)
      setCopied(false)
      setError(null)
      void qc.invalidateQueries({ queryKey: invitesQuery.queryKey })
    },
    onError: (e) => setError(e.message),
  })
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { response } = await api.DELETE('/admin/invites/{id}', { params: { path: { id } } })
      if (!response.ok) throw new Error(`revoke failed: ${response.status}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesQuery.queryKey }),
  })

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const email = String(form.get('email') ?? '').trim()
    create.mutate({
      role: form.get('role') === 'guest' ? 'guest' : 'member',
      ...(email ? { email } : {}),
      max_uses: Number(form.get('max_uses') || 1),
      expires_in_hours: Number(form.get('expires_in_hours') || 168),
    })
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 p-6">
      <section>
        <h1 className="text-lg font-semibold">Invites</h1>
        <p className="mb-4 text-sm text-fg-muted">Registration is invite-only. Links are shown once; copy them before leaving the page.</p>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-bg-muted p-4">
          <label className="col-span-2 space-y-1 text-sm">
            <span className="font-medium">Email (optional)</span>
            <Input name="email" type="email" placeholder="Binds the link to this address and emails it" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Role</span>
            <select name="role" defaultValue="member" className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm">
              <option value="member">Member</option>
              <option value="guest">Guest</option>
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Max uses</span>
            <Input name="max_uses" type="number" min={1} max={1000} defaultValue={1} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Expires in (hours)</span>
            <Input name="expires_in_hours" type="number" min={1} max={2160} defaultValue={168} />
          </label>
          <div className="flex items-end">
            <Button type="submit" disabled={create.isPending} className="w-full">
              {create.isPending ? 'Creating…' : 'Create invite link'}
            </Button>
          </div>
          {error && (
            <p role="alert" className="col-span-2 text-sm text-danger">
              {error}
            </p>
          )}
        </form>

        {created && (
          <div className="mt-3 space-y-2 rounded-lg border border-accent/40 bg-accent/10 p-4 text-sm">
            <p className="font-medium">Invite link created{created.emailed ? ' and emailed' : ''}. This is the only time it will be shown.</p>
            <div className="flex gap-2">
              <Input readOnly value={created.url} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <Button variant="secondary" onClick={() => copy(created.url)}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">All invites</h2>
        {invites.isPending && <p className="text-sm text-fg-muted">Loading…</p>}
        {invites.isError && <p className="text-sm text-danger">Couldn't load invites.</p>}
        {invites.data?.length === 0 && <p className="text-sm text-fg-muted">No invites yet.</p>}
        <ul className="divide-y divide-border rounded-lg border border-border">
          {invites.data?.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
              <div className="min-w-0">
                <div className="truncate">
                  <span className="font-medium capitalize">{i.role}</span>
                  {i.email ? <span className="text-fg-muted"> · {i.email}</span> : <span className="text-fg-muted"> · open link</span>}
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${i.status === 'active' ? 'bg-accent/15' : 'bg-border text-fg-muted'}`}>{i.status}</span>
                </div>
                <div className="text-xs text-fg-muted">
                  {i.uses}/{i.max_uses} used · expires {new Date(i.expires_at).toLocaleString()}
                </div>
              </div>
              {i.status === 'active' && (
                <Button variant="secondary" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(i.id)}>
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
